import React from 'react';
import {Link} from 'react-router-dom';
import LegalLayout, {LegalSection, LegalContactCard} from '../components/redesign/LegalLayout';
import '../assets/scss/redesign.scss';

const TermsAndConditions = () => (
    <LegalLayout title="Terms and Conditions">
        <p>
            These Terms and Conditions ("Terms") govern your access to and use of the website operated by
            Strategic Wellness Advocates LLC ("we," "us," or "our"). By accessing or using this website,
            submitting a contact form, or otherwise communicating with us through the site, you agree to be
            bound by these Terms and our <Link to="/privacy-policy">Privacy Policy</Link>. If you do not agree,
            please do not use the website.
        </p>
        <div className="sw-legal__callout sw-legal__callout--warn">
            <strong>
                If you are experiencing a medical or mental health emergency, call 911 or go to the nearest
                emergency room.
            </strong>{' '}
            You can also call or text 988 to reach the Suicide and Crisis Lifeline. This website is not
            monitored around the clock and should not be used for urgent requests.
        </div>

        <LegalSection title="1. Informational Purposes Only">
            <p>
                The content on this website is provided for general informational purposes about our organization
                and the behavioral health services we offer. It does not constitute medical, psychological, or
                clinical advice, diagnosis, or treatment, and it is not a substitute for consultation with a
                qualified healthcare professional. Information on this site does not create a provider–patient
                relationship.
            </p>
        </LegalSection>

        <LegalSection title="2. No Provider–Patient Relationship Through the Website">
            <p>
                Submitting a contact form, sending a message, or requesting information through this website does
                not establish a provider–patient relationship with Strategic Wellness Advocates LLC or any of its
                clinicians. A treatment relationship is formed only after a completed intake, assessment, and
                written agreement to receive services.
            </p>
        </LegalSection>

        <LegalSection title="3. Communications and SMS Consent">
            <p>
                By providing your phone number and checking the consent box on our contact form, you agree to
                receive Customer Care SMS text messages from Strategic Wellness Advocates LLC. SMS, message,
                text, and texting are used for customer care purposes only, such as responding to your inquiry,
                scheduling, and appointment reminders.
            </p>
            <p><strong>Standard Messaging Disclosures:</strong></p>
            <ul>
                <li>Message and data rates may apply.</li>
                <li>You can opt out at any time by texting <strong>"STOP."</strong></li>
                <li>
                    For assistance, text <strong>"HELP"</strong> or visit our{' '}
                    <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
                    <Link to="/terms-and-conditions">Terms and Conditions</Link> pages.
                </li>
                <li>Message frequency may vary.</li>
                <li>SMS/message and text/texting are used for customer care.</li>
            </ul>
            <p>
                Communications sent through the website contact form or by standard email and text message are
                not encrypted and may not be secure. Please do not include sensitive health, treatment, or
                substance use details in these messages. Our intake team will collect that information through
                secure channels.
            </p>
        </LegalSection>

        <LegalSection title="4. Eligibility and Accurate Information">
            <p>
                You must be at least 18 years old to submit a contact form on your own behalf. Inquiries about
                services for children or adolescents must be submitted by a parent or legal guardian. You agree
                that any information you provide to us is accurate, current, and complete.
            </p>
        </LegalSection>

        <LegalSection title="5. Acceptable Use">
            <p>You agree not to:</p>
            <ul>
                <li>Use the website for any unlawful purpose or in violation of any applicable law or regulation</li>
                <li>Submit false, misleading, harassing, abusive, or threatening content</li>
                <li>Attempt to gain unauthorized access to the website, its servers, or any connected systems</li>
                <li>Introduce viruses, malware, or other harmful code</li>
                <li>Copy, scrape, or reproduce website content for commercial purposes without written permission</li>
            </ul>
        </LegalSection>

        <LegalSection title="6. Intellectual Property">
            <p>
                All content on this website, including text, graphics, logos, images, and design, is the property
                of Strategic Wellness Advocates LLC or its licensors and is protected by copyright, trademark, and
                other intellectual property laws. You may view and print pages for personal, non-commercial use
                only. No other use is permitted without our prior written consent.
            </p>
        </LegalSection>

        <LegalSection title="7. Third-Party Links">
            <p>
                This website may contain links to third-party websites, including social media platforms and
                mapping services. These links are provided for convenience only. We do not control and are not
                responsible for the content, accuracy, or privacy practices of any third-party site.
            </p>
        </LegalSection>

        <LegalSection title="8. Disclaimer of Warranties">
            <p>
                This website and its content are provided "as is" and "as available" without warranties of any
                kind, express or implied, including warranties of merchantability, fitness for a particular
                purpose, accuracy, or non-infringement. We do not warrant that the website will be uninterrupted,
                error-free, or free of viruses or other harmful components.
            </p>
        </LegalSection>

        <LegalSection title="9. Limitation of Liability">
            <p>
                To the fullest extent permitted by law, Strategic Wellness Advocates LLC and its owners,
                employees, contractors, and agents shall not be liable for any direct, indirect, incidental,
                consequential, special, or punitive damages arising out of or related to your use of, or
                inability to use, this website or any content on it, even if we have been advised of the
                possibility of such damages.
            </p>
        </LegalSection>

        <LegalSection title="10. Indemnification">
            <p>
                You agree to indemnify and hold harmless Strategic Wellness Advocates LLC and its owners,
                employees, and agents from any claims, losses, liabilities, damages, costs, or expenses,
                including reasonable attorneys' fees, arising from your use of the website or your violation of
                these Terms.
            </p>
        </LegalSection>

        <LegalSection title="11. Governing Law">
            <p>
                These Terms are governed by and construed in accordance with the laws of the State of Maryland,
                without regard to its conflict of law principles. Any dispute arising from these Terms or your
                use of the website shall be brought exclusively in the state or federal courts located in
                Baltimore City, Maryland, and you consent to the jurisdiction of those courts.
            </p>
        </LegalSection>

        <LegalSection title="12. Changes to These Terms">
            <p>
                We may revise these Terms at any time by posting an updated version on this page with a new
                effective date. Your continued use of the website after changes are posted constitutes your
                acceptance of the revised Terms.
            </p>
        </LegalSection>

        <LegalSection title="13. Severability">
            <p>
                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions
                will remain in full force and effect.
            </p>
        </LegalSection>

        <LegalSection title="14. Contact Us">
            <p>Questions about these Terms may be directed to:</p>
            <LegalContactCard/>
        </LegalSection>
    </LegalLayout>
);

export default TermsAndConditions;
